import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Navigate, Link } from "react-router-dom";
import { Crown, Sparkles, Users, MessageCircle, Loader2, Trophy, MapPin, Calendar, Heart, Send, Lock } from "lucide-react";
import { toast } from "sonner";
import MediaSlider from "@/components/MediaSlider";

function ApplySection({ onApplied }) {
  const [form, setForm] = useState({ reason: "", instagram: "", city: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/vip/apply", form);
      toast.success("Application submitted — admin will review");
      onApplied?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="glass rounded-3xl p-6 space-y-3" data-testid="vip-apply-form">
      <div className="font-display uppercase text-2xl">Apply for VIP</div>
      <div className="text-xs text-muted">VIP Club is currently invite/approval based. Tell us who you are — admin reviews within 48h.</div>
      <textarea required rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why should you be in the room? (a line or two)" className="input-dark resize-none" data-testid="vip-apply-reason" />
      <div className="grid grid-cols-2 gap-3">
        <input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="@instagram (optional)" className="input-dark" data-testid="vip-apply-instagram" />
        <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="input-dark" data-testid="vip-apply-city" />
      </div>
      <button disabled={busy} className="btn-primary w-full justify-center" data-testid="vip-apply-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />} Submit Application
      </button>
    </form>
  );
}

function MatchmakingWaitlist() {
  return (
    <div className="glass rounded-3xl p-6" data-testid="matchmaking-live" style={{ background: "linear-gradient(135deg, rgba(255,46,154,0.16), rgba(123,47,255,0.18))" }}>
      <div className="chip !text-lime !border-lime/40 mb-2"><span className="w-1.5 h-1.5 bg-lime rounded-full animate-pulse mr-1" /> ClubVIP Matchmaking · Live</div>
      <div className="font-fancy uppercase text-3xl leading-tight mb-2">Meet your people.</div>
      <div className="text-sm text-muted mb-5 max-w-md">Private-by-default matchmaking for approved VIPs. Only first name + blurred photo + top 3 interests are visible until you both hit Love Match.</div>
      <div className="grid grid-cols-3 gap-2 mb-5 text-center">
        <div><div className="font-fancy text-2xl text-magenta leading-none">3</div><div className="text-[9px] font-mono uppercase tracking-widest text-muted mt-1">public photos</div></div>
        <div><div className="font-fancy text-2xl text-lime leading-none">1</div><div className="text-[9px] font-mono uppercase tracking-widest text-muted mt-1">live selfie</div></div>
        <div><div className="font-fancy text-2xl text-skyx leading-none">∞</div><div className="text-[9px] font-mono uppercase tracking-widest text-muted mt-1">love matches</div></div>
      </div>
      <Link to="/clubvip" data-testid="mm-open-clubvip" className="btn-primary w-full justify-center">
        <Heart className="w-4 h-4" fill="currentColor" /> Open ClubVIP →
      </Link>
      <div className="text-[9px] font-mono uppercase tracking-widest text-muted text-center mt-3">VIP approval required · Private by default</div>
    </div>
  );
}

function OpportunitiesGrid() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wl, setWl] = useState(null);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/opportunities/public").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  const requestAccess = async (id) => {
    try { await api.post(`/opportunities/${id}/request-access`); toast.success("Request sent — admin will reach out"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const joinWaitlist = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post(`/opportunities/${wl.id}/waitlist`, form);
      toast.success("You're on the waiting list — we'll confirm on WhatsApp");
      setWl(null); setForm({ name: "", mobile: "", email: "", note: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  if (loading) return <div className="glass rounded-3xl p-6 font-mono text-sm text-muted">Loading opportunities…</div>;
  if (items.length === 0) return <div className="glass rounded-3xl p-6 font-mono text-sm text-muted">No open opportunities right now. Watch this space</div>;
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="opportunities-grid">
      {items.map((o) => (
        <div key={o.id} className="glass rounded-3xl p-5 card-lift" data-testid={`opp-${o.id}`}>
          <div className="chip !text-lime !border-lime/40 mb-2">{String(o.type || "").replace(/_/g, " ")}</div>
          <div className="font-display text-2xl leading-tight">{o.name}</div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted mt-1"><Calendar className="inline w-3 h-3 mr-1 text-lime" />{o.date} · <MapPin className="inline w-3 h-3 mx-1 text-magenta" />{o.city}</div>
          <div className="text-sm text-text/80 mt-2 whitespace-pre-wrap">{o.description}</div>
          <MediaSlider links={[...(o.images || []), ...(o.videos || [])]} testId={`opp-media-${o.id}`} />
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={() => requestAccess(o.id)} data-testid={`opp-request-${o.id}`} className="btn-pink !py-2 !px-4 !text-xs">Request Access</button>
            <button onClick={() => setWl(o)} data-testid={`opp-waitlist-${o.id}`} className="btn-lime !py-2 !px-4 !text-xs">Join waiting list</button>
            <a href={`https://wa.me/917250120666?text=${encodeURIComponent(`Hi Eventom! I want in on ${o.name} (${o.city}, ${o.date}).`)}`} target="_blank" rel="noreferrer"
              data-testid={`opp-whatsapp-${o.id}`} className="btn-ghost !py-2 !px-4 !text-xs"><MessageCircle className="w-3.5 h-3.5" /> Chat with admin</a>
          </div>
        </div>
      ))}
      {wl && (
        <div className="fixed inset-0 z-[85] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setWl(null)}>
          <form onSubmit={joinWaitlist} onClick={(e) => e.stopPropagation()} className="glass p-6 w-full max-w-sm space-y-3" data-testid="opp-waitlist-form">
            <div className="font-display text-3xl leading-tight">Join the list</div>
            <div className="text-xs text-muted">{wl.name} · {wl.city} · {wl.date}</div>
            <input required data-testid="wl-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" />
            <input required data-testid="wl-mobile" placeholder="WhatsApp number" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="input-dark" />
            <input type="email" data-testid="wl-email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark" />
            <textarea rows={2} data-testid="wl-note" placeholder="Anything we should know? (plus ones, city, timing)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="input-dark resize-none" />
            <button disabled={busy} className="btn-lime w-full justify-center" data-testid="wl-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Join waiting list</button>
          </form>
        </div>
      )}
    </div>
  );
}

function CommunityPreview() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/community/posts").then(({ data }) => setPosts(data.slice(0, 5))).catch(() => {}); }, []);
  const [text, setText] = useState(""); const [busy, setBusy] = useState(false);
  const post = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post("/community/posts", { text }); setText(""); const { data } = await api.get("/community/posts"); setPosts(data.slice(0, 5)); toast.success("Posted"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="glass rounded-3xl p-6" data-testid="vip-community-preview">
      <div className="chip bg-lime/10 text-lime mb-2">Community</div>
      <div className="font-display uppercase text-2xl mb-3">The insider feed</div>
      <form onSubmit={post} className="mb-4">
        <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 500))} rows={2} placeholder="Post something to the VIP feed…" className="input-dark w-full resize-none !text-sm" data-testid="vip-community-input" />
        <div className="flex justify-between items-center mt-2">
          <span className="text-[10px] font-mono text-muted">{text.length}/500</span>
          <button disabled={busy || !text.trim()} className="btn-primary !py-1.5 !px-3 !text-xs" data-testid="vip-community-submit"><Send className="w-3 h-3" /> Post</button>
        </div>
      </form>
      <div className="space-y-2">
        {posts.map((p) => (
          <div key={p.id} className="rounded-2xl bg-white/5 p-3" data-testid={`vip-post-${p.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-full bg-pink text-white text-xs flex items-center justify-center font-bold">{p.user_name?.[0]}</div>
              <div className="font-bold text-xs">{p.user_name}</div>
              <div className="text-[10px] font-mono text-muted">{new Date(p.created_at).toLocaleDateString()}</div>
            </div>
            <div className="text-sm text-text/85">{p.text}</div>
          </div>
        ))}
        {posts.length === 0 && <div className="text-xs text-muted font-mono">Be the first to post</div>}
      </div>
      <Link to="/community" className="mt-3 inline-flex text-[11px] font-mono uppercase text-lime underline">Open full feed →</Link>
    </div>
  );
}

function ContestsPreview() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/contests").then(({ data }) => setItems(data.filter((c) => c.status === "active").slice(0, 3))).catch(() => {}); }, []);
  return (
    <div className="glass rounded-3xl p-6" data-testid="vip-contests-preview">
      <div className="chip bg-lime/10 text-lime mb-2">Contests</div>
      <div className="font-display uppercase text-2xl mb-3">Win the chaos.</div>
      <div className="space-y-2">
        {items.map((c) => (
          <Link to="/contests" key={c.id} className="block rounded-2xl bg-white/5 p-3 card-lift" data-testid={`vip-contest-${c.id}`}>
            <div className="flex items-center gap-2 mb-1"><Trophy className="w-4 h-4 text-lime" /><div className="font-display uppercase">{c.name}</div></div>
            <div className="text-xs text-muted">Prize: <span className="text-lime">{c.prize}</span> · Ends {c.end_date}</div>
          </Link>
        ))}
        {items.length === 0 && <div className="text-xs text-muted font-mono">No active contests. Check back soon.</div>}
      </div>
      <Link to="/contests" className="mt-3 inline-flex text-[11px] font-mono uppercase text-lime underline">See all contests →</Link>
    </div>
  );
}

export default function VipPage() {
  const { user, loading } = useAuth();
  const [appStatus, setAppStatus] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get("/vip/apply/me").then(({ data }) => setAppStatus(data.status)).catch(() => setAppStatus("not_applied"));
  }, [user]);

  if (loading) return <Layout><div className="p-10">Loading...</div></Layout>;

  const isVip = !!user?.vip;

  return (
    <Layout>
      <section className="relative overflow-hidden border-b border-line" style={{ background: "radial-gradient(ellipse at top, rgba(123,47,255,0.35), transparent 60%), radial-gradient(ellipse at bottom, rgba(255,46,154,0.28), transparent 60%), #0A0A0A" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16 grid md:grid-cols-2 items-center gap-8">
          <div>
            <div className="chip bg-pink/20 text-pink mb-3" data-testid="vip-title-chip">Eventom Club VIP</div>
            <h1 className="font-fancy uppercase text-5xl md:text-6xl leading-[0.9]">The event ends.<br/><span className="marker-lime">The vibe</span><br/>doesn't.</h1>
            <p className="mt-4 text-text/85 max-w-md">Where the real night begins — <b className="text-lime">exclusive after-parties</b>, VIP access, and moments the crowd never sees.</p>
            <div className="mt-6 flex flex-wrap gap-6">
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-muted">After-parties</div></div>
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-muted">VIP access</div></div>
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-muted">Premium moments</div></div>
            </div>
            {isVip ? (
              <div className="mt-6 inline-block glass rounded-3xl px-6 py-3 border border-lime/30" data-testid="vip-active-card">
                <div className="font-mono text-[10px] uppercase tracking-widest text-lime">VIP Active</div>
                <div className="font-display uppercase text-2xl mt-1">You're in.</div>
                <Link to="/clubvip" data-testid="vip-goto-clubvip" className="btn-primary mt-3 inline-flex !py-2 !px-4 !text-xs"><Crown className="w-3.5 h-3.5" /> Open ClubVIP matchmaking →</Link>
              </div>
            ) : (
              <a href="#apply" data-testid="vip-hero-cta" className="btn-lime mt-6"><Crown className="w-4 h-4" /> Join the VIP →</a>
            )}
            <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-muted">Exclusive access · Limited invites</div>
          </div>
          <div className="relative">
            <img src="/vip-hero.png" alt="Eventom VIP" loading="eager" className="w-full rounded-3xl shadow-[0_40px_120px_rgba(255,46,154,0.35)]" data-testid="vip-hero-image" />
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-6">
        {!user && (
          <>
            <MatchmakingWaitlist />
            <div className="glass p-6 text-center" data-testid="vip-login-cta">
              <div className="font-display text-3xl">Log in to apply for VIP.</div>
              <p className="text-sm text-muted mt-2">You can still browse open after parties and trips below, and join the waiting list.</p>
              <Link to="/login" className="btn-lime mt-4 inline-flex" data-testid="vip-login-link">Log in / Sign up</Link>
            </div>
          </>
        )}
        {user && !isVip && (
          <>
            {appStatus === "pending" ? (
              <div className="glass rounded-3xl p-8 text-center" data-testid="vip-app-pending">
                <Lock className="w-8 h-8 mx-auto text-orangex mb-2" />
                <div className="font-display uppercase text-2xl">Application under review</div>
                <div className="text-sm text-muted mt-2">Admin will get back to you within 48 hours. Once approved, this page unlocks.</div>
              </div>
            ) : appStatus === "rejected" ? (
              <div className="glass rounded-3xl p-8 text-center" data-testid="vip-app-rejected">
                <div className="font-display uppercase text-2xl">Application not approved</div>
                <div className="text-sm text-muted mt-2">You can reapply anytime.</div>
                <div className="mt-4"><ApplySection onApplied={() => setAppStatus("pending")} /></div>
              </div>
            ) : (
              <ApplySection onApplied={() => setAppStatus("pending")} />
            )}
            <div className="grid md:grid-cols-2 gap-6">
              <ContestsPreview />
              <MatchmakingWaitlist />
            </div>
          </>
        )}

        {isVip && (
          <>
            <div className="grid md:grid-cols-2 gap-6">
              <ContestsPreview />
              <CommunityPreview />
            </div>
            <MatchmakingWaitlist />
          </>
        )}

        <div data-testid="vip-opportunities-section">
          <div className="chip !text-pink !border-pink/40 mb-3">VIP Opportunities</div>
          <div className="font-display text-4xl mb-2">After parties · House parties · Trips</div>
          <p className="text-sm text-muted mb-4">{isVip ? "Request access and we'll confirm your spot." : "Not a member yet? Join the waiting list or chat with us on WhatsApp — members get first call."}</p>
          <OpportunitiesGrid />
        </div>
      </section>
    </Layout>
  );
}
